// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Container,
    IconContainer,
} from 'common/components/Status/Styles';
import { ReactComponent as RoundedArrowRight } from 'common/icons/rounded-arrow-right.svg';
import { ReactComponent as RoundedX } from 'common/icons/rounded-x.svg';
import { ReactComponent as RoundedQuestion } from 'common/icons/rounded-question.svg';
import { ReactComponent as RoundedSquare } from 'common/icons/rounded-square.svg';
import { ReactComponent as RoundedPause } from 'common/icons/rounded-pause.svg';
import { ReactComponent as RoundedCircle } from 'common/icons/rounded-circle.svg';
import { ReactComponent as RoundedBlock } from 'common/icons/rounded-block.svg';
import { Link } from 'react-router-dom';
import { COLORS } from 'common/theme';

interface IStatusConfig {
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    color: string;
}

export enum StatusType {
    SUCCESS = 'success',
    FAILED = 'failed',
    UNAVAILABLE = 'unavailable',
    NO_STATE = 'noState',
    NOT_EXIST = 'notExist',
    STOPPED = 'stopped',
    PAUSED = 'paused',
}

const STATUSES: Record<StatusType, IStatusConfig> = {
    [StatusType.SUCCESS]: {
        icon: RoundedArrowRight,
        color: COLORS.SUCCESS,
    },
    [StatusType.FAILED]: {
        icon: RoundedX,
        color: COLORS.DANGER,
    },
    [StatusType.UNAVAILABLE]: {
        icon: RoundedQuestion,
        color: COLORS.GREY_70,
    },
    [StatusType.NO_STATE]: {
        icon: RoundedCircle,
        color: COLORS.GREY_70,
    },
    [StatusType.NOT_EXIST]: {
        icon: RoundedBlock,
        color: COLORS.GREY_70,
    },
    [StatusType.STOPPED]: {
        icon: RoundedSquare,
        color: COLORS.INFO,
    },
    [StatusType.PAUSED]: {
        icon: RoundedPause,
        color: COLORS.GREY_80,
    },
};

interface IProps {
    value: StatusType;
    label: React.ReactNode;
    to?: string;
    iconOnly?: boolean;
}

export default function Status({
    value,
    label,
    to,
    iconOnly,
}: IProps) {
    const {
        icon: Icon,
        color,
    } = STATUSES[value];

    return (
        <Container>
            <IconContainer $color={color}>
                <Icon/>
            </IconContainer>

            {!iconOnly && (to ? <Link to={to}>{label}</Link> : <span>{label}</span>)}
        </Container>
    );
}
